<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { v4 as uuidv4 } from 'uuid';
import {
  ComputeTaskCreateDocument,
  CouponApplicationInput,
  SampleType,
} from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BCollapse,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BFormTextarea,
  BModal,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import {
  COUPON_TYPE_NAMES,
  COUPON_UNITS,
  COMPUTE_TASK_TYPES,
} from '@/constants';
import CodeValidation, { DefaultCode } from '@/components/CodeValidation.vue';
import { useUserStore } from '@/store/user';
import { Attributes_NextFlowPipeline } from '@/utils/compute-utils';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import DeveloperOptions from '@/components/compute/DeveloperOptions.vue';
import PipelinePicker from '@/components/compute/PipelinePicker.vue';
import { PipelineType } from '@/gql/graphql';

const userStore = useUserStore();

// --- Component references ---
const codeValidation = ref<InstanceType<typeof CodeValidation>>();
const devOptions = ref<InstanceType<typeof DeveloperOptions>>();

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  projectId: number;
  projectName: string;
  unitsRequired: number;
  defaultCodes: DefaultCode[];
  defaultName: string;
  samples: SampleType[];
}>();

const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

const computeName = ref<string>(props.defaultName);
const computeDescription = ref<string>('');
const computeUnitsRequired = computed(() => {
  return selectedSampleIndices.value.length;
});

const customCloudRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);

const advancedOptionsShow = ref<boolean>(false);

const adminPipelineBarcodeWhitelist = ref<string>('');
const adminPipelineReferenceOrganism = ref<string>('');
const adminPipelineUmiLength = ref<number>(8);
const adminPipelineUmiLengthOverride = ref<boolean>(false);

// --- NextFlow Pipeline specific state ---
const selectedPipeline = ref<PipelineType | null>(null);
const nfCustomWorkflow = ref<boolean>(false);
const nfWorkflowLocation = ref<string>('');

// --- Sample Selection (select all samples by default) ---
const selectedSampleIndices = ref<number[]>(props.samples.map((_, i) => i));

function selectAllSamples() {
  selectedSampleIndices.value = props.samples.map((_, i) => i);
}
function clearAllSamples() {
  selectedSampleIndices.value = [];
}

const selectedSamplesCount = computed(() => selectedSampleIndices.value.length);

// --- ComputeTaskCreate Mutation ---
const mutationError = ref<string | null>(null);
const mutationInProgress = ref<boolean>(false);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ComputeTaskCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskCreate) {
    console.debug('Sample reconstruction started:', res.data.computeTaskCreate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      emits('closeAndRefresh');
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'Sample reconstruction start failed: ' + res.data;
    console.error('Sample reconstruction start failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error starting sample reconstruction:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function mutationRequested() {
  mutationInProgress.value = true;
  mutationError.value = null;
  mutationShowSuccess.value = false;

  if (devOptions.value?.extraAttributesInvalid) {
    mutationError.value =
      'Developer JSON attributes must be a valid JSON object.';
    mutationInProgress.value = false;
    return;
  }

  // validate code requirements
  if (!codeValidation.value || codeValidation.value.failure) {
    console.error('Invalid kit or coupon code', codeValidation.value);
    mutationError.value = 'Please provide a valid kit or coupon code.';
    mutationInProgress.value = false;
    return;
  }

  // initialiaze coupon applications
  const couponApplications: CouponApplicationInput[] =
    codeValidation?.value?.codeValidationItems.map((item) => ({
      code: item.codeString,
      unitsDeclared: item.unitsDeclared,
    })) || [];

  // initialize attributes
  let attributes: Attributes_NextFlowPipeline = {
    gceRequestId: uuidv4(),
    samples: [],
  };

  // make sure at least one sample is selected
  if (selectedSampleIndices.value.length === 0) {
    mutationError.value = 'Please select at least one sample.';
    mutationInProgress.value = false;
    return;
  }

  // select samples and populate attributes
  let selectedSamples = selectedSampleIndices.value.map(
    (i) => props.samples[i]
  );

  // populate samples
  selectedSamples.forEach((sample: SampleType) => {
    // validate sample
    if (!sample.name) {
      console.error('Sample name not provided:', sample);
      mutationError.value = 'Please provide a name for each sample.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.fileR1 || !sample.fileR2) {
      console.error('Sample files not provided:', sample);
      (mutationError.value =
        'Please provide both R1 and R2 files for each sample. Sample '),
        sample.name,
        ' is missing files.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.bucket || !sample.bucket.name) {
      console.error('Sample bucket not provided:', sample);
      (mutationError.value =
        'Please provide a bucket for each sample. Sample '),
        sample.name,
        ' is missing a bucket.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.kit?.barcode) {
      console.error('Barcode not provided:', sample);
      (mutationError.value =
        'Please provide a barcode for each sample. Sample '),
        sample.name,
        ' is missing a barcode.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.kit?.umiLength) {
      console.error('UMI length not provided:', sample);
      (mutationError.value =
        'Please specify UMI length for each sample. Sample '),
        sample.name,
        ' is missing the UMI length specification.';
      mutationInProgress.value = false;
      return;
    }
    // push sample to collection
    attributes.samples.push({
      name: sample.name,
      fileR1: sample.fileR1,
      fileR2: sample.fileR2,
      bucketInputName: sample.bucket?.name,
      barcodesWhitelist: sample.kit?.barcode,
      umiLength: sample.kit?.umiLength,
      reference: 'jurkat',
      referenceTranscriptomeProfile: 'jurkat',
    });
  });

  // ADMIN OPTIONS
  if (userStore.admin && advancedOptionsShow.value) {
    // barcode whitelist override
    if (adminPipelineBarcodeWhitelist.value) {
      attributes.samples.forEach((sample) => {
        sample.barcodesWhitelist = adminPipelineBarcodeWhitelist.value;
      });
    }

    // reference organism override
    if (adminPipelineReferenceOrganism.value) {
      attributes.samples.forEach((sample) => {
        // see if comma-separated values were provided
        // - split the string by comma
        // - trim all items by white-space and make it an array
        // - filter out empty items
        const referenceOrganismItems = adminPipelineReferenceOrganism.value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '');

        // if more than one item, use the array
        if (referenceOrganismItems.length > 0) {
          sample.referenceOrganism = referenceOrganismItems;
        }
      });
    }

    // umi length override
    if (adminPipelineUmiLengthOverride.value) {
      attributes.samples.forEach((sample) => {
        sample.umiLength = adminPipelineUmiLength.value;
      });
    }
  }

  // CATALOG PIPELINE
  if (nfCustomWorkflow.value) {
    attributes.workflowLocation = nfWorkflowLocation.value;
  } else if (selectedPipeline.value && selectedPipeline.value.id) {
    attributes.pipelineId = selectedPipeline.value.id;
  }

  // DEVELOPER OPTIONS
  if (userStore.developer && devOptions.value?.expanded) {
    // general - version
    if (devOptions.value.version) {
      attributes.version = devOptions.value.version;
    }

    // general - vm lifecycle
    if (devOptions.value.vmLifecycle) {
      attributes.vmLifecycle = devOptions.value.vmLifecycle;
    }

    // general - machine type
    if (devOptions.value.machineType) {
      attributes.machineType = devOptions.value.machineType;
    }

    // data pipeline - min pipeline
    if (devOptions.value.dataPipelineMin) {
      attributes.minPipelineRequested = true;
    }

    // data pipeline - copy data
    if (devOptions.value.dataPipelineCopyData) {
      attributes.needsDataCopying = true;
    }

    // extra attributes from JSON
    if (devOptions.value.extraAttributes) {
      Object.assign(attributes, devOptions.value.extraAttributes);
    }
  }

  console.debug(
    'New sample reconstruction requested (NextFlow), task attributes:',
    attributes
  );

  // mutationInProgress.value = false;
  executeMutation({
    name: computeName.value,
    description: computeDescription.value,
    coupons: couponApplications,
    projectId: props.projectId,
    taskType: COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE,
    attributes: attributes,
    requestedRegionPrefix: customCloudRegionPrefix.value
      ? customCloudRegionPrefix.value
      : undefined,
  });
}

function onModalHide(evt: Event) {
  // prevent closing modal if mutation is in progress
  if (mutationInProgress.value) {
    evt.preventDefault();
    return;
  }
  // otherwise
  emits('close');
}
</script>
<template>
  <div>
    <b-modal
      v-model="show"
      :backdrop="mutationInProgress ? 'static' : true"
      :no-close-on-esc="mutationInProgress"
      @hide="onModalHide"
      class="modal-lg"
    >
      <!-- Modal Title -->
      <template #title>
        <font-awesome-icon icon="wand-magic-sparkles" class="me-2" />
        <strong>{{ props.projectName }}</strong>
        <br />
        <span style="font-size: medium">
          <b-badge variant="warning">
            <i class="fab fa-neos"></i>
            NextFlow
          </b-badge>
        </span>
      </template>

      <!-- Modal Content -->
      <div>
        <!-- Reconstruction label -->
        <h5>Run identification</h5>
        <BFormGroup class="mt-3">
          <label for="compute-name">
            <font-awesome-icon :icon="['fas', 'tag']" class="me-1" />
            Reconstruction label
            <font-awesome-icon
              :icon="['fas', 'question-circle']"
              class="ms-2 text-secondary"
              id="compute-name-help-icon"
            />
            <b-tooltip target="compute-name-help-icon"
              >Label to help you identify the reconstruction run later.
            </b-tooltip>
          </label>
          <BFormInput
            v-model="computeName"
            id="compute-name"
            name="compute-name"
            placeholder="Enter reconstruction label"
            required
          ></BFormInput>
        </BFormGroup>

        <b-form-group class="mt-3">
          <label for="bucket-description">
            <font-awesome-icon :icon="['fas', 'align-justify']" class="me-1" />
            Description
            <font-awesome-icon
              :icon="['fas', 'question-circle']"
              class="ms-2 text-secondary"
              id="compute-description-help-icon"
            />
            <b-tooltip target="compute-description-help-icon">
              Optional description of the run for later reference.
            </b-tooltip>
          </label>
          <b-form-textarea
            id="compute-description"
            name="computeDescription"
            v-model="computeDescription"
            type="text"
            rows="2"
            max-rows="10"
          ></b-form-textarea>
        </b-form-group>

        <!-- NextFlow Workflow: Pipeline Selection -->
        <h5 class="mt-4 mb-1">
          <font-awesome-icon :icon="['fas', 'server']" class="me-2" />
          Pipeline selection
        </h5>

        <PipelinePicker
          class="mt-3"
          v-model="selectedPipeline"
          :enabled="!nfCustomWorkflow"
          :label="null"
          :type="null"
          :premium="null"
          :activeOnly="true"
          :allowNullPipeline="false"
        />

        <!-- Custom Nextflow Workflow-->
        <template v-if="userStore.developer">
          <!-- UMI Length -->
          <label class="mt-3">
            Custom Workflow (developer option)
            <font-awesome-icon
              :icon="['fas', 'question-circle']"
              id="nf-workflow-location-help"
              class="ms-2 text-secondary"
            />
            <BTooltip target="nf-workflow-location-help" class="large-tooltip">
              <div style="text-align: justify">
                <p>Specify the NextFlow workflow URL. Accepted options are:</p>
                <ul>
                  <li>
                    <code>
                      https://gitlab.samplinghuman.com/group/project/-/tree/ref/workflow-path
                    </code>
                  </li>
                  <li>
                    <code>
                      https://gitlab.samplinghuman.com/group/project/-/blob/ref/workflow-path/main.nf
                    </code>
                  </li>
                  <li>
                    <code>
                      gitlab://group/project/-/raw/ref/workflow-path/main.nf
                    </code>
                  </li>
                  <li><code>gs://bucket/workflow-path/main.nf</code></li>
                </ul>
                <p>
                  Best default for humans is to use the GitLab browser URL from
                  the repository page. The backend canonicalizes that to the raw
                  workflow source internally. Example browser URL:
                  <code>
                    https://gitlab.samplinghuman.com/samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/tree/v0.1.0-dev
                  </code>
                  is automatically converted to canonical form:
                  <code>
                    gitlab://samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/raw/v0.1.0-dev/main.nf
                  </code>
                  by the backend.
                </p>
              </div>
            </BTooltip>
          </label>
          <BFormGroup>
            <BInputGroup>
              <!-- Prepend override checkbox -->
              <template #prepend>
                <BButton variant="outline-secondary">
                  <BFormCheckbox
                    v-model="nfCustomWorkflow"
                    name="nf-custom-workflow"
                  ></BFormCheckbox>
                </BButton>
              </template>
              <!-- Custom Workflow Location -->
              <BFormInput
                v-model="nfWorkflowLocation"
                id="nf-workflow-location"
                name="nf-workflow-location"
                placeholder="Enter workflow location"
                :disabled="!nfCustomWorkflow"
                @change="nfCustomWorkflow = true"
              ></BFormInput>
            </BInputGroup>
          </BFormGroup>
        </template>
        <!-- end of Custom NextFlow workflow options -->

        <!-- Samples Selection -->
        <h5 class="mt-4 mb-1">
          <font-awesome-icon :icon="['fas', 'list-check']" class="me-2" />
          Samples to process
        </h5>
        <small class="text-muted mb-1">
          {{ selectedSamplesCount }} of {{ props.samples.length }}
          {{ props.samples.length === 1 ? 'sample' : 'samples' }} selected (<a
            href="#"
            class="text-decoration-none text-secondary"
            @click.prevent="selectAllSamples"
          >
            select all
            <font-awesome-icon
              :icon="['fas', 'check-double']"
              class="ms-1" /></a
          >,
          <a
            href="#"
            class="text-decoration-none text-secondary"
            @click.prevent="clearAllSamples"
          >
            clear all
            <font-awesome-icon :icon="['fas', 'xmark']" class="ms-1 me-1" /></a
          >)
        </small>

        <!-- Samples Checkbox List; v-model bound to array of indices -->
        <BFormGroup>
          <div class="row">
            <div
              class="col-12 col-md-6"
              v-for="(sample, index) in props.samples"
              :key="sample.id ?? sample.name ?? index"
            >
              <BFormCheckbox
                v-model="selectedSampleIndices"
                :value="index"
                name="pipeline-sample-checkbox"
                switch
                class="my-1"
              >
                {{ sample.name || `Sample ${index + 1}` }}
              </BFormCheckbox>
            </div>
          </div>
        </BFormGroup>

        <!-- Code Validation - Compute credits associated with kit(s) or coupon(s) -->
        <h5 class="mt-4">Kit validation</h5>
        <p>
          <small>
            Kits associate your samples with the correct barcodes and make sure
            the subsequent processing is accurate. Kits also contain storage
            credits that allow you to upload data to the Sampling Human
            analytics cloud in the first place. Please do not exchange the
            correct kit or coupon codes if you run out of credits and contact
            support for assistance instead. This will ensure valid code
            association and prevent any disruptions in your workflow or data
            processing.
          </small>
        </p>
        <CodeValidation
          class="mt-3"
          ref="codeValidation"
          label-text="Kit
        or coupon code"
          button-text="Apply"
          :defaultCodes="props.defaultCodes"
          :requireKit="false"
          :requireExplicitCounts="false"
          :requiredCouponTypeName="COUPON_TYPE_NAMES.COMPUTE"
          :requriedCouponUnitsName="COUPON_UNITS.SAMPLES"
          :requiredCouponUnitsCount="computeUnitsRequired"
          :requiredLicenseTypeName="null"
          :immediateValidation="true"
          :hideComponentMessage="false"
        />

        <!-- Disclaimer -->
        <b-alert
          v-if="codeValidation"
          v-model="codeValidation.success"
          variant="success"
          class="mt-3"
        >
          Please double-check the kit/coupon code before proceeding. Beware that
          <strong>{{ computeUnitsRequired }} compute credits</strong> for sample
          reconstruction will be deducted from the provided kit/coupon.
        </b-alert>
      </div>

      <!-- Advanced Options -->
      <div
        :class="[
          'mt-4',
          advancedOptionsShow ? 'expanded-group' : 'collapsed-group',
        ]"
      >
        <BButton
          variant="outline-dark"
          class="w-100"
          size="sm"
          @click="advancedOptionsShow = !advancedOptionsShow"
        >
          <font-awesome-icon :icon="['fas', 'cogs']" class="me-2" />
          Advanced Options
          <font-awesome-icon
            :icon="['fas', advancedOptionsShow ? 'angle-up' : 'angle-down']"
            class="ms-2"
          />
        </BButton>

        <BCollapse v-model="advancedOptionsShow">
          <!-- Reference Organism -->
          <BFormGroup class="mt-3">
            <label>
              Reference organism
              <font-awesome-icon
                id="reference-organism-icon"
                icon="question-circle"
                class="ms-2 text-secondary"
              />
              <b-tooltip target="reference-organism-icon">
                The reference organism value specifies the original organism
                from which the cells were obtained. It serves for alignment of
                the sequences to genome.
                <strong>Its correct specification is critical.</strong>
                Current options are `human` and `mouse`. By default, the value
                is set to `human`.
              </b-tooltip>
            </label>
            <BFormInput
              v-model="adminPipelineReferenceOrganism"
              id="pipeline-reference-organism"
              name="pipeline-reference-organism"
              placeholder="Enter sample reference organism (e.g. mouse) or leave blank for default (human)"
            ></BFormInput>
          </BFormGroup>

          <!-- Barcode whitelist -->
          <BFormGroup class="mt-3">
            <label>
              Custom barcodes whitelist
              <font-awesome-icon
                id="custom-barcode-icon"
                icon="question-circle"
                class="ms-2 text-secondary"
              />
              <b-tooltip target="custom-barcode-icon">
                Specify the custom barcodes whitelist to override the default
                option. See sample details for the default barcodes whitelist.
              </b-tooltip>
            </label>
            <BFormInput
              v-model="adminPipelineBarcodeWhitelist"
              id="pipeline-barcode-whitelist"
              name="pipeline-barcode-whitelist"
              placeholder="Enter custom barcodes whitelist or leave blank for default"
            ></BFormInput>
          </BFormGroup>

          <!-- UMI Length -->
          <label class="mt-3">
            UMI Length
            <font-awesome-icon
              id="custom-umi-length-icon"
              icon="question-circle"
              class="ms-2 text-secondary"
            />
            <b-tooltip target="custom-umi-length-icon">
              Specify the custom UMI length to override the default option. See
              sample details for the default UMI length.
            </b-tooltip>
          </label>
          <BFormGroup>
            <BInputGroup>
              <!-- Prepend override checkbox -->
              <template #prepend>
                <BButton variant="outline-secondary">
                  <BFormCheckbox
                    v-model="adminPipelineUmiLengthOverride"
                    name="pipeline-umi-length-override"
                  ></BFormCheckbox>
                </BButton>
              </template>
              <!-- UMI Length Input -->
              <BFormInput
                v-model.number="adminPipelineUmiLength"
                @change="adminPipelineUmiLengthOverride = true"
                type="number"
                id="pipeline-umi-length"
                name="pipeline-umi-length"
              ></BFormInput>
            </BInputGroup>
          </BFormGroup>
        </BCollapse>
      </div>

      <!-- Developer options -->
      <DeveloperOptions
        v-if="userStore.developer"
        ref="devOptions"
        size="sm"
        class="mt-3"
        :computeTaskType="COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE"
      />

      <!-- Error Message -->
      <b-alert
        v-model="mutationError"
        variant="danger"
        dismissible
        class="mt-3"
        @dismissed="mutationError = null"
      >
        {{ mutationError }}
      </b-alert>

      <!-- Cloud Region -->
      <CloudRegionSelection
        v-model="customCloudRegionPrefix"
        v-model:modelRequired="customCloudRegionRequired"
        class="mt-3"
      />

      <!-- Modal Footer with Action Buttons -->
      <template #footer>
        <b-button
          variant="secondary"
          @click="onModalHide"
          :disabled="mutationInProgress"
        >
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="mutationRequested"
          :disabled="
            codeValidation?.failure || mutationInProgress || mutationShowSuccess
          "
        >
          <span v-if="mutationInProgress">
            Starting Reconstruction
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Reconstruction Started
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Start Reconstruction </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
